import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { AnnotationMode, getDocument, renderTextLayer, type PDFDocumentProxy } from 'pdfjs-dist'
import 'pdfjs-dist/build/pdf.worker.entry'
import { Popup } from 'devextreme-react/popup'
import Button from 'devextreme-react/button'
import TextBox from 'devextreme-react/text-box'
import DataGrid, { Column, Editing } from 'devextreme-react/data-grid'
import { LoadIndicator } from 'devextreme-react/load-indicator'
import './ExtractionReviewPdf.css'

export interface ExtractionReviewField {
	name: string
	label: string
}

export interface ExtractionReviewGridColumn {
	dataField: string
	caption: string
	dataType?: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'object'
}

export interface ExtractionReviewGridConfig {
	rowsField: string
	keyExpr: string
	columns: ExtractionReviewGridColumn[]
	heading?: string
}

export interface ExtractionReviewUi {
	titlePrefix: string
	intro: string
	fields: ExtractionReviewField[]
	grid?: ExtractionReviewGridConfig
	requiredField?: string
	confirmLabel?: string
	discardLabel?: string
}

export interface ExtractionReviewData {
	field_confidence?: Record<string, { confidence?: number; reason?: string }>
	needs_human_review?: boolean
	errors?: string[]
	[key: string]: unknown
}

export interface ExtractionReview {
	filename: string
	extraction_run_id?: number | null
	file_hash?: string | null
	data: ExtractionReviewData
	ui: ExtractionReviewUi
}

export interface ExtractionReviewDialogProps {
	reviews: ExtractionReview[]
	process?: string | null
	confirming?: boolean
	onConfirm: (data: ExtractionReviewData, review: ExtractionReview) => void
	onDiscard: (review: ExtractionReview) => void
	onHide: () => void
}

type PdfStatus = 'missing' | 'loading' | 'ready' | 'error'

const MIN_ZOOM = 0.5
const MAX_ZOOM = 3
const ZOOM_STEP = 0.25

function stepZoom(current: number, direction: 1 | -1): number {
	const next = Math.round((current + direction * ZOOM_STEP) * 100) / 100
	return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
}

function isRenderCancelled(error: unknown): boolean {
	return (
		typeof error === 'object' &&
		error !== null &&
		'name' in error &&
		(error as { name?: string }).name === 'RenderingCancelledException'
	)
}

function pageData(review: ExtractionReview): ExtractionReviewData {
	const data = { ...review.data }
	const grid = review.ui.grid
	if (grid) {
		const rows = review.data[grid.rowsField]
		data[grid.rowsField] = Array.isArray(rows) ? [...rows] : []
	}
	return data
}

function PdfPane(props: { review: ExtractionReview; process?: string | null }) {
	const { review, process } = props
	const [status, setStatus] = useState<PdfStatus>(review.file_hash && process ? 'loading' : 'missing')
	const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null)
	const [zoom, setZoom] = useState(1)
	const [paneWidth, setPaneWidth] = useState(0)
	const scrollRef = useRef<HTMLDivElement>(null)
	const pagesRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const el = scrollRef.current
		if (!el) {
			return
		}
		const update = () => {
			const width = el.clientWidth
			setPaneWidth(current => (current === width ? current : width))
		}
		update()
		const observer = new ResizeObserver(update)
		observer.observe(el)
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		setZoom(1)
		if (!review.file_hash || !process) {
			setPdfDocument(null)
			setStatus('missing')
			return
		}

		let cancelled = false
		let activePdf: PDFDocumentProxy | null = null
		setPdfDocument(null)
		setStatus('loading')
		const hash = encodeURIComponent(review.file_hash)
		const processName = encodeURIComponent(process)
		axios({
			method: 'get',
			url: `${API_FIBER_URL}/imports/file/${hash}/download?process=${processName}`,
			responseType: 'arraybuffer',
		})
			.then(response => {
				const bytes = new Uint8Array(response.data as ArrayBuffer)
				return getDocument({ data: bytes.slice(0), isEvalSupported: false }).promise
			})
			.then(pdf => {
				if (cancelled) {
					pdf.destroy()
					return
				}
				activePdf = pdf
				setPdfDocument(pdf)
				setStatus('ready')
			})
			.catch(() => {
				if (!cancelled) {
					setPdfDocument(null)
					setStatus('error')
				}
			})

		return () => {
			cancelled = true
			activePdf?.destroy()
		}
	}, [review.file_hash, process])

	useEffect(() => {
		const pdf = pdfDocument
		const host = pagesRef.current
		const scroll = scrollRef.current
		if (!pdf || !host || !scroll || status !== 'ready' || paneWidth < 20) {
			return
		}

		let cancelled = false
		const tasks: Array<{ cancel: () => void }> = []

		const render = async () => {
			const first = await pdf.getPage(1)
			if (cancelled) {
				return
			}
			const unscaled = first.getViewport({ scale: 1 })
			const fit = Math.max(scroll.clientWidth - 24, 1) / unscaled.width
			const scale = fit * zoom
			host.replaceChildren()

			for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
				if (cancelled) {
					return
				}
				const page = pageNumber === 1 ? first : await pdf.getPage(pageNumber)
				if (cancelled) {
					return
				}
				const viewport = page.getViewport({ scale })
				const pageEl = document.createElement('div')
				pageEl.className = 'extraction-review-pdf-page'
				pageEl.style.width = `${viewport.width}px`
				pageEl.style.height = `${viewport.height}px`

				const canvas = document.createElement('canvas')
				const outputScale = window.devicePixelRatio || 1
				canvas.width = Math.floor(viewport.width * outputScale)
				canvas.height = Math.floor(viewport.height * outputScale)
				canvas.style.width = `${viewport.width}px`
				canvas.style.height = `${viewport.height}px`
				const context = canvas.getContext('2d')
				if (!context) {
					continue
				}
				pageEl.appendChild(canvas)

				const textLayer = document.createElement('div')
				textLayer.className = 'textLayer'
				pageEl.appendChild(textLayer)
				host.appendChild(pageEl)

				const renderTask = page.render({
					canvasContext: context,
					viewport,
					annotationMode: AnnotationMode.DISABLE,
					...(outputScale === 1 ? {} : { transform: [outputScale, 0, 0, outputScale, 0, 0] }),
				})
				tasks.push(renderTask)
				await renderTask.promise
				if (cancelled) {
					return
				}

				const textContent = await page.getTextContent()
				if (cancelled) {
					return
				}
				const textTask = renderTextLayer({
					textContentSource: textContent,
					container: textLayer,
					viewport,
				})
				tasks.push(textTask)
				await textTask.promise
			}
		}

		render().catch((error: unknown) => {
			if (cancelled || isRenderCancelled(error)) {
				return
			}
			setStatus('error')
		})

		return () => {
			cancelled = true
			tasks.forEach(task => task.cancel())
			host.replaceChildren()
		}
	}, [paneWidth, pdfDocument, status, zoom])

	return (
		<div className="extraction-review-pdf">
			<div className="extraction-review-pdf-toolbar">
				<Button
					icon="minus"
					hint="Zoom out"
					stylingMode="text"
					disabled={status !== 'ready' || zoom <= MIN_ZOOM}
					onClick={() => setZoom(current => stepZoom(current, -1))}
				/>
				<span className="extraction-review-pdf-zoom">{Math.round(zoom * 100)}%</span>
				<Button
					icon="plus"
					hint="Zoom in"
					stylingMode="text"
					disabled={status !== 'ready' || zoom >= MAX_ZOOM}
					onClick={() => setZoom(current => stepZoom(current, 1))}
				/>
			</div>
			<div className="extraction-review-pdf-scroll" ref={scrollRef}>
				{status === 'ready' ? (
					<div ref={pagesRef} />
				) : (
					<div className="extraction-review-pdf-status">
						{status === 'loading' && <LoadIndicator height={28} width={28} />}
						{status === 'missing' && <span>The source PDF is not available for this review.</span>}
						{status === 'error' && <span>Could not load the PDF.</span>}
					</div>
				)}
			</div>
		</div>
	)
}

export default function ExtractionReviewDialog(props: ExtractionReviewDialogProps) {
	const { reviews, process = null, confirming = false, onConfirm, onDiscard, onHide } = props
	const [index, setIndex] = useState(0)
	const safeIndex = reviews.length === 0 ? 0 : Math.min(index, reviews.length - 1)
	const review = reviews[safeIndex]
	const [data, setData] = useState<ExtractionReviewData>(() => (review ? pageData(review) : {}))

	useEffect(() => {
		setIndex(i => (reviews.length === 0 ? 0 : Math.min(i, reviews.length - 1)))
	}, [reviews.length])

	useEffect(() => {
		if (review) {
			setData(pageData(review))
		}
	}, [review?.filename])

	if (!review) {
		return null
	}

	const {
		titlePrefix,
		intro,
		fields,
		grid,
		requiredField,
		confirmLabel = 'Confirm',
		discardLabel = 'Discard',
	} = review.ui
	const requiredOk =
		!requiredField ||
		(data[requiredField] !== undefined && data[requiredField] !== null && String(data[requiredField]).trim() !== '')
	const rows = grid ? (data[grid.rowsField] as Record<string, unknown>[]) ?? [] : []
	const pageLabel = `${safeIndex + 1} of ${reviews.length}`
	const lowConfidence = (field: string) => {
		const score = review.data.field_confidence?.[field]?.confidence
		return score !== undefined && score < 80
	}

	return (
		<Popup
			visible={true}
			showTitle={true}
			title={`${titlePrefix} — ${review.filename} (${pageLabel})`}
			showCloseButton={true}
			dragEnabled={true}
			resizeEnabled={true}
			width={'94dvw'}
			minWidth={'720px'}
			height={'90dvh'}
			position="center"
			hideOnOutsideClick={false}
			onHiding={onHide}
			contentRender={() => (
				<div
					style={{
						display: 'flex',
						gap: 16,
						height: 'calc(90dvh - 240px)',
						minHeight: 0,
						overflow: 'hidden',
					}}
				>
					<PdfPane key={`${review.filename}-${review.file_hash ?? ''}`} review={review} process={process} />
					<div
						style={{
							flex: '1 1 380px',
							minWidth: 0,
							minHeight: 0,
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
							<Button
								text="Previous"
								disabled={confirming || safeIndex === 0}
								onClick={() => setIndex(safeIndex - 1)}
							/>
							<span>{pageLabel}</span>
							<Button
								text="Next"
								disabled={confirming || safeIndex >= reviews.length - 1}
								onClick={() => setIndex(safeIndex + 1)}
							/>
						</div>
						<div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingRight: 4 }}>
							<p>{intro}</p>
							{review.data.needs_human_review && (
								<p style={{ color: '#b54708' }}>
									Some fields could not be validated automatically. Please review carefully.
								</p>
							)}
							{!!review.data.errors?.length && (
								<ul style={{ color: '#b54708' }}>
									{review.data.errors.map((error, i) => (
										<li key={i}>{error}</li>
									))}
								</ul>
							)}
							<div
								style={{
									display: 'grid',
									gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
									gap: 12,
									marginBottom: 16,
								}}
							>
								{fields.map(field => (
									<TextBox
										key={`${review.filename}-${field.name}`}
										label={field.label}
										value={String(data[field.name] ?? '')}
										onValueChanged={(e: any) =>
											setData(prev => ({ ...prev, [field.name]: e.value }))
										}
										elementAttr={
											lowConfidence(field.name)
												? { style: 'border: 1px solid #f5a623' }
												: undefined
										}
									/>
								))}
							</div>
							{grid && (
								<>
									<h3 style={{ marginTop: 0 }}>{grid.heading ?? 'Line items'}</h3>
									<DataGrid
										dataSource={rows}
										keyExpr={grid.keyExpr}
										showBorders={true}
										rowAlternationEnabled={true}
										allowColumnResizing={true}
										width="100%"
										onRowUpdated={(e: any) =>
											setData(prev => ({
												...prev,
												[grid.rowsField]: (
													(prev[grid.rowsField] as Record<string, unknown>[]) ?? []
												).map(row =>
													row[grid.keyExpr] === e.key ? { ...row, ...e.data } : row
												),
											}))
										}
									>
										<Editing mode="cell" allowUpdating={true} />
										{grid.columns.map(column => (
											<Column
												key={column.dataField}
												dataField={column.dataField}
												caption={column.caption}
												dataType={column.dataType}
											/>
										))}
									</DataGrid>
								</>
							)}
						</div>
						<div style={{ marginTop: 12, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
							<Button text={discardLabel} onClick={() => onDiscard(review)} disabled={confirming} />
							<Button
								text={confirmLabel}
								type="success"
								onClick={() => onConfirm(data, review)}
								disabled={confirming || !requiredOk}
							/>
						</div>
					</div>
				</div>
			)}
		/>
	)
}
